import { Box, TextField, Typography } from '@mui/material';
import { businessData as businessDataAtom, userDetails as userDetailsAtom, UserRoleAtom } from 'atoms';
import Button from 'common/Button';
import ButtonLarge from 'common/ButtonLarge';
import Heading from 'common/Heading';
import PasswordInput from 'common/PasswordInput';
import routes from 'config/routes';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import supabaseHandler from 'utils/SupaBase';

import React, { useEffect, useState } from 'react';

const getSupabase = () => {
    
    if (!supabaseHandler) {
        console.error('Supabase supabaseHandler not initialized - check environment variables');
        return null;
    }
    return supabaseHandler;
};
const SignUp = () => {
    const [searchParams] = useSearchParams();

    const [formValues, setFormValues] = useState({
        firstName: '',
        lastName: '',
        businessName: '',
        email: '',
        password: '',
        confirmPassword: '',
    });
    const [error, setError] = useState('');
    const [userRole, setUserRole] = useRecoilState(UserRoleAtom);

    useEffect(() => {
        setFormValues({
            firstName: searchParams.get('firstName') || '',
            lastName: searchParams.get('lastName') || '',
            email: searchParams.get('email') || '',
            businessName: searchParams.get('businessName') || '',
        });
    }, [searchParams]);

    const [showPasswordCriteria, setShowPasswordCriteria] = useState(false);
    const [userPresent, setUserPresent] = useState(false);
    const [passwordCriteria, setPasswordCriteria] = useState({
        length: false,
        uppercase: false,
        number: false,
        specialChar: false,
    });
    const [formValid, setFormValid] = useState(false);
    const [loading, setLoading] = useState(false);

    const setUser = useSetRecoilState(userDetailsAtom);
    const setBusiness = useSetRecoilState(businessDataAtom);

    const navigate = useNavigate();

    const handlePasswordBlur = () => {
        if (formValues.password) {
            setShowPasswordCriteria(true);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Trim email value to remove leading/trailing whitespace
        const processedValue = name === 'email' ? value.trim() : value;
        const updatedValues = { ...formValues, [name]: processedValue };
        setFormValues(updatedValues);

        // Check if form is valid
        validateForm(updatedValues, name === 'password' ? value : null);
    };

    const validateForm = (values, password = null) => {
        const { firstName, lastName, email, businessName, password: currentPassword, confirmPassword } = values;

        const isPasswordValid = password
            ? {
                  length: password.length >= 8,
                  uppercase: /[A-Z]/.test(password),
                  number: /\d/.test(password),
                  specialChar: /[.!@#$%^&*()_+\-=]/.test(password),
              }
            : passwordCriteria;

        const isFormValid =
            firstName &&
            lastName &&
            businessName &&
            email &&
            currentPassword &&
            confirmPassword &&
            currentPassword === confirmPassword &&
            isPasswordValid.length &&
            isPasswordValid.uppercase &&
            isPasswordValid.number &&
            isPasswordValid.specialChar;

        setPasswordCriteria(isPasswordValid);
        setFormValid(isFormValid);
    };

    const handleSignUp = async (email, password, firstName, lastName) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/auth/v1/signup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email,
                    password,
                    first_name: firstName,
                    last_name: lastName,
                    emailRedirectTo: `https://provider-dev.beseen.health/login`, // Custom redirect
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.error || errorData.message || 'Signup failed';
                console.error('Error signing up:', errorMessage);
                setError(errorMessage);
                return null;
            }

            const result = await response.json();
            // Backend should return data in format similar to Supabase: { user: {...}, session: {...} }
            // Adjust based on your backend response structure
            return result.data || result;
        } catch (error) {
            console.error('Error signing up:', error.message);
            setError(error.message || 'An error occurred during signup');
            return null;
        }
    };
    const checkIfUserExist = async (businessName, uuid, accessToken) => {
        if (!accessToken) {
            throw new Error('No authentication token found');
        }
        
        try {
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/get-business`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    },
                    body: JSON.stringify({ userid: uuid })
                }
            );
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to check if user exists');
            }
            
            const result = await response.json();
            
            // Handle both array and object responses
            if (result.success && result.data !== undefined) {
                const businessData = Array.isArray(result.data) ? result.data : (result.data ? [result.data] : []);
                return businessData;
            } else if (Array.isArray(result)) {
                return result;
            } else {
                return [];
            }
        } catch (error) {
            console.error('Error checking if user exists:', error);
            throw error;
        }
    };
    const addDataToBusiness = async (businessName, uuid, accessToken) => {
        if (!accessToken) {
            throw new Error('No authentication token found');
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/business`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    },
                    body: JSON.stringify({
                        userid: uuid,
                        name: businessName
                    })
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('Error creating business:', errorData.error || 'Failed to create business');
                throw new Error(errorData.error || 'Failed to create business');
            }

            const result = await response.json();
            
            // Handle response - could be object or array
            if (result.success && result.data !== undefined) {
                const businessData = Array.isArray(result.data) 
                    ? (result.data.length > 0 ? result.data[0] : null)
                    : (result.data || null);
                
                if (!businessData) {
                    throw new Error('Business data not returned from API');
                }
                
                return businessData;
            } else {
                throw new Error('Unexpected response format from business API');
            }
        } catch (error) {
            console.error('Error creating business:', error);
            throw error;
        }
    };
    const addUserToOfficeStaff = async (email, businessId, userId) => {
        const supabase = getSupabase();
        if (!supabase) throw new Error('Authentication service not available');

        const { data, error } = await supabase
            .from('office_staff')
            .update({ user_id: userId })
            .eq('email', email.toLowerCase())
            .eq('business_id', businessId)
            .select('*')
            .single();

        if (error) {
            throw new Error(error.message);
        }
        return data;
    };
    const addUserToProvider = async (email, businessId, userId) => {
        const supabase = getSupabase();
        if (!supabase) throw new Error('Authentication service not available');

        const { error } = await supabase
            .from('provider')
            .update({ user_id: userId })
            .eq('email', email.toLowerCase())
            .eq('business_id', businessId);
        if (error) {
            throw new Error(error.message);
        }
        // .single();
    };
    const addDataToProfile = async (userId) => {
        if (userId) {
            const supabase = getSupabase();
            if (!supabase) throw new Error('Authentication service not available');
            
            let { data, error } = await supabase.from('profiles').update({ app_id: 'provider' }).eq('id', userId);
            if (error) {
                throw new Error(error.message);
            }
        }
    };

    const loginUser = async (email, password, businessId) => {
        try {
            // Call backend API for login
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/auth/v1/token`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        grant_type: 'password',
                        email,
                        password,
                    }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.error || errorData.message || 'Login failed';
                console.error('Error logging in:', errorMessage);
                setError(errorMessage);
                return;
            }

            const result = await response.json();
            // Backend should return data in format similar to Supabase: { user: {...}, session: {...} }
            const data = result.data || result;

            if (!data?.user) {
                console.error('Error logging in: Invalid response');
                setError('Login failed');
                return;
            }

            const uid = data?.user?.id;
            const accessToken = data?.session?.access_token || data?.access_token;
            const refreshToken = data?.session?.refresh_token || data?.refresh_token;
            
            // Set Supabase session so AuthContext can access the token immediately
            const supabase = getSupabase();
            if (supabase && accessToken && refreshToken) {
                const { error: sessionError } = await supabase.auth.setSession({
                    access_token: accessToken,
                    refresh_token: refreshToken,
                });

                if (sessionError) {
                    console.error('Error setting Supabase session:', sessionError);
                } else {
                    await addDataToProfile(uid);
                }
            }

            localStorage.setItem('user', JSON.stringify(data));
            setUser(data);
            
            // Dispatch custom event to notify AuthContext of the update
            window.dispatchEvent(new Event('auth-update'));
            
            let businessData = [];

            if (businessId) {
                // Fetch business by ID via backend API
                if (!accessToken) {
                    console.error('No access token available for business fetch');
                    setError('Authentication error. Please try again.');
                    return;
                }
                
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_SERVER_URL}/api/providers/get-business`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${accessToken}`
                            },
                            body: JSON.stringify({ id: businessId })
                        }
                    );
                    
                    if (!response.ok) {
                        const errorData = await response.json().catch(() => ({}));
                        console.error('Error fetching business:', errorData.error || 'Failed to fetch business');
                        setError('Failed to fetch business data');
                        return;
                    }
                    
                    const result = await response.json();
                    const business = Array.isArray(result.data) 
                        ? (result.data.length > 0 ? result.data[0] : null)
                        : (result.data || null);
                    
                    if (business) {
                        setBusiness(business);
                        businessData = [business];
                    }
                } catch (error) {
                    console.error('Error fetching business:', error);
                    setError('Failed to fetch business data');
                    return;
                }
            } else {
                // Fetch business by userid via backend API
                if (!accessToken) {
                    console.error('No access token available for business fetch');
                    setError('Authentication error. Please try again.');
                    return;
                }
                
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_SERVER_URL}/api/providers/get-business`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${accessToken}`
                            },
                            body: JSON.stringify({ userid: uid })
                        }
                    );
                    
                    if (!response.ok) {
                        const errorData = await response.json().catch(() => ({}));
                        console.error('Error fetching business:', errorData.error || 'Failed to fetch business');
                        setError('Failed to fetch business data');
                        return;
                    }
                    
                    const result = await response.json();
                    // Handle both array and object responses
                    const business = Array.isArray(result.data) 
                        ? (result.data.length > 0 ? result.data[0] : null)
                        : (result.data || null);
                    
                    if (business) {
                        setBusiness(business);
                        businessData = [business];
                    } else {
                        businessData = [];
                    }
                } catch (error) {
                    console.error('Error fetching business:', error);
                    setError('Failed to fetch business data');
                    return;
                }
            }
            if (businessData?.length > 0) {
                // navigate(routes.panel.business);

                navigate(routes.termsAndConditions);
            } else {
                setError('Something Went Wrong! Try to login with your credentials');
            }
        } catch (error) {
            console.error('Error logging in:', error.message);
            setError(error.message || 'Login failed');
        }
    };
    const handleSubmit = async () => {
        if (formValues.password) {
            setShowPasswordCriteria(true);
        }
        setLoading(true);
        try {
            const { firstName, lastName, email, businessName, password, confirmPassword } = formValues;

            if (searchParams?.size === 6) {
                const userData = await handleSignUp(email, password, firstName, lastName);
                if (!userData?.user?.id) throw new Error('User signup failed');
                const isProvider = searchParams.get('provider');
                const businessId = searchParams.get('businessId');
                if (isProvider === 'true') {
                    const data = await addUserToProvider(email, businessId, userData?.user?.id);
                } else {
                    const data = await addUserToOfficeStaff(email, businessId, userData?.user?.id);
                    const role = data?.role_id === 1 ? 'Admin' : data?.role_id === 2 ? 'General' : '';
                    localStorage.setItem('userRole', role);
                    setUserRole(role);
                }

                await loginUser(email, password, businessId);
                // navigate(routes.termsAndConditions);
            } else {
                const userData = await handleSignUp(email, password, firstName, lastName);
                if (!userData?.user?.id) throw new Error('User signup failed');

                // Get access token from signup response
                const accessToken = userData?.session?.access_token || userData?.access_token;
                if (!accessToken) {
                    throw new Error('No access token received from signup');
                }

                const business = await checkIfUserExist(businessName, userData?.user?.id, accessToken);
                //add data to profile table
                //addDataToProfile(userData?.user?.id,userData?.user?.email);
                //user already exist
                if (business.length > 0) {
                    setUserPresent(true);
                } else {
                    // accessToken is already available from checkIfUserExist call above
                    const addToBusiness = await addDataToBusiness(businessName, userData?.user?.id, accessToken);
                    const businessId = addToBusiness?.id;
                    await loginUser(email, password, businessId);
                    // navigate(routes.termsAndConditions);
                }
            }
        } catch (error) {            return;
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col gap-10 items-center w-full">
            <div className="flex flex-col gap-7 items-center w-full">
                <div className="flex flex-col gap-1 items-center">
                    <Heading className="text-center">Create an Account</Heading>
                    <span className="text-base text-center font-poppins w-[90%]">
                        Be seen and regain control of your practice.
                    </span>
                </div>

                <div className="flex flex-col gap-4 w-full">
                    <div className="flex flex-col lg:flex-row gap-2">
                        <input
                            type="text"
                            placeholder="First Name"
                            value={formValues.firstName}
                            className="common-input p-2.5 px-5 focus:!bg-white w-full lg:1/2"
                            name="firstName"
                            required={true}
                            onChange={handleInputChange}
                            autoFocus={true}
                            disabled={searchParams.size}
                        />

                        <input
                            type="text"
                            placeholder="Last Name"
                            value={formValues.lastName}
                            className="common-input p-2.5 px-5 focus:!bg-white w-full lg:1/2"
                            name="lastName"
                            required={true}
                            onChange={handleInputChange}
                        />
                    </div>

                    <input
                        type="text"
                        placeholder="Business Name"
                        value={formValues.businessName}
                        className="common-input p-2.5 px-5 focus:!bg-white"
                        name="businessName"
                        required={true}
                        onChange={handleInputChange}
                        disabled={searchParams.size}
                    />
                    <input
                        type="email"
                        placeholder="Email"
                        value={formValues.email}
                        className="common-input p-2.5 px-5 focus:!bg-white"
                        name="email"
                        required={true}
                        onChange={handleInputChange}
                        disabled={searchParams.size}
                    />

                    <PasswordInput
                        placeholder="Password"
                        value={formValues.password}
                        onChange={handleInputChange}
                        name="password"
                        onBlur={handlePasswordBlur}
                    />
                    {showPasswordCriteria && formValues.password && (
                        <Box>
                            <h3>Password must include:</h3>
                            <ul style={{ listStyleType: 'disc', marginLeft: '20px' }}>
                                <li style={{ color: passwordCriteria.length ? 'green' : 'red' }}>
                                    <Typography variant="caption">Minimum 8 characters</Typography>
                                </li>

                                <li style={{ color: passwordCriteria.uppercase ? 'green' : 'red' }}>
                                    <Typography variant="caption">At least 1 uppercase letter</Typography>
                                </li>

                                <li style={{ color: passwordCriteria.number ? 'green' : 'red' }}>
                                    <Typography variant="caption">At least 1 digit</Typography>
                                </li>

                                <li style={{ color: passwordCriteria.specialChar ? 'green' : 'red' }}>
                                    <Typography variant="caption">
                                        At least 1 special character (.!@#$%^&*()_+-=)
                                    </Typography>
                                </li>
                            </ul>
                        </Box>
                    )}
                    {formValues.password && (
                        <PasswordInput
                            placeholder="Re-enter Password"
                            value={formValues.confirmPassword}
                            onChange={handleInputChange}
                            name="confirmPassword"
                        />
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-4 w-full items-center">
                <ButtonLarge
                    onClick={handleSubmit}
                    disabled={!formValid}
                    className="w-full font-bold"
                    loading={loading}
                >
                    Create Account
                </ButtonLarge>{' '}
                {userPresent && (
                    <Box>
                        <br />
                        <Typography variant="caption" color="red">
                            User already exist in the system!
                        </Typography>
                    </Box>
                )}
                <p className="help-text text-center">
                    Already have an account?{' '}
                    <span
                        className="text-primary font-semibold cursor-pointer hover:underline"
                        onClick={() => navigate(routes.auth.login)}
                    >
                        Sign In
                    </span>
                </p>
                {error && <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>}
            </div>
        </div>
    );
};

export default SignUp;
